import React, { createContext, useCallback, useContext, useMemo, useRef, useState, useEffect } from 'react';
import { createDemoAudioBlob } from '@/utils/createDemoAudio';
import { useAudioCache } from './useAudioCache';

export type FocusTrack = {
  id: string;
  name: string;
  description?: string;
  src: string;
  isBest?: boolean;
  category: string;
  duration?: string;
  mood?: string;
};

interface FocusMusicContextValue {
  tracks: FocusTrack[];
  currentTrack: FocusTrack;
  isPlaying: boolean;
  volume: number; // 0..1
  play: (trackId?: string) => Promise<void>;
  pause: () => void;
  stop: () => void;
  setTrack: (trackId: string) => Promise<void>;
  setVolume: (v: number) => void;
  toggle: () => Promise<void>;
  // Caching support
  cacheAllTracks: () => Promise<void>;
  isCaching: boolean;
  cacheProgress: number; // 0..1
}

const FocusMusicContext = createContext<FocusMusicContextValue | undefined>(undefined);

const TRACKS: FocusTrack[] = [
  // Best Picks - Real Music
  { id: 'best', name: 'Best for Learning — Lo‑Fi Focus', description: 'Recommended', src: '/audio/focus/best-lofi.mp3', isBest: true, category: 'Best Picks', duration: '30min', mood: 'calm' },
  { id: 'ambient-study', name: 'Ambient Study Music', description: 'Deep focus soundscape', src: '/audio/focus/ambient-study.mp3', isBest: true, category: 'Best Picks', duration: '25min', mood: 'focused' },
  
  // Piano & Classical
  { id: 'peaceful-piano', name: 'Peaceful Piano', description: 'Gentle piano for concentration', src: '/audio/focus/peaceful-piano.mp3', category: 'Piano', duration: '15min', mood: 'peaceful' },
  { id: 'ambient-piano-1', name: 'Ambient Piano Music', description: 'Soft piano with ambient textures', src: '/audio/focus/ambient-piano-1.mp3', category: 'Piano', duration: '12min', mood: 'calm' },
  { id: 'classical-bach', name: 'Bach for Focus', description: 'Baroque precision for deep work', src: '/audio/focus/classical-bach.mp3', category: 'Classical', duration: '45min', mood: 'focused' },
  
  // Ambient & Electronic
  { id: 'ambiance-guitar', name: 'Ambient Guitar', description: 'Gentle guitar atmospheres', src: '/audio/focus/ambiance-guitar.mp3', category: 'Ambient', duration: '18min', mood: 'relaxed' },
  { id: 'lofi-beats', name: 'Lo‑Fi Hip Hop', description: 'Chill beats for studying', src: '/audio/focus/lofi-beats.mp3', category: 'Electronic', duration: '35min', mood: 'relaxed' },
  
  // Nature Sounds
  { id: 'nature-rain', name: 'Gentle Rain', description: 'Steady rainfall on leaves', src: '/audio/focus/nature-rain.mp3', category: 'Nature', duration: '45min', mood: 'calming' },
  { id: 'ocean-waves', name: 'Ocean Waves', description: 'Rhythmic waves for focus', src: '/audio/focus/ocean-waves.mp3', category: 'Nature', duration: '30min', mood: 'calming' },
  { id: 'nature-forest', name: 'Forest Ambience', description: 'Deep forest soundscape', src: '/audio/focus/nature-forest.mp3', category: 'Nature', duration: '40min', mood: 'natural' },
  
  // Specialized Focus
  { id: 'piano-solo', name: 'Solo Piano Meditation', description: 'Peaceful piano compositions', src: '/audio/focus/piano-solo.mp3', category: 'Instrumental', duration: '38min', mood: 'peaceful' },
  { id: 'brown-noise', name: 'Brown Noise Deep', description: 'Deep brown noise for focus', src: '/audio/focus/brown-noise.mp3', category: 'Focus Sounds', duration: '60min', mood: 'grounding' },
  
  // Demo fallback (only if real music fails)
  { id: 'demo-ambient', name: 'Demo Ambient', description: 'Generated ambient sound', src: 'data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmMcBDuZ3/HCeBYDLX3A8tyiWBIOVLDq66JSGQRKuOLu02oSAi+G0uKJB8/CrFM', category: 'Demo', duration: '1min', mood: 'demo' },
];

export const FocusMusicProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [currentId, setCurrentId] = useState<string>('best');
  const [isPlaying, setIsPlaying] = useState(false);
  const [volume, setVolumeState] = useState<number>(0.6);

  const [isCaching, setIsCaching] = useState(false);
  const [cacheProgress, setCacheProgress] = useState(0);
  
  const { cacheAudio, getCachedAudio, isSupported: isCacheSupported } = useAudioCache();

  const currentTrack = useMemo(() => TRACKS.find(t => t.id === currentId) || TRACKS[0], [currentId]);

  const ensureAudio = () => {
    if (!audioRef.current) {
      // Prefer the hidden <audio> element rendered by the provider.
      // As a fallback (in case ref isn't ready yet), create and append one.
      const el = document.createElement('audio');
      el.setAttribute('preload', 'auto');
      el.setAttribute('playsinline', '');
      el.loop = true;
      el.volume = volume;
      (el.style as any).display = 'none';
      document.body.appendChild(el);
      el.addEventListener('ended', () => setIsPlaying(false));
      el.addEventListener('error', (e) => {
        console.error('Audio error:', e);
        setIsPlaying(false);
      });
      audioRef.current = el;
    }
    return audioRef.current as HTMLAudioElement;
  };

  const loadSrcIfNeeded = async (audio: HTMLAudioElement, track: FocusTrack) => {
    // First check for cached version
    if (isCacheSupported) {
      const cachedUrl = await getCachedAudio(track.id);
      if (cachedUrl && audio.src !== cachedUrl) {
        console.log('Loading cached audio track:', track.name);
        audio.src = cachedUrl;
        try { audio.load(); } catch (error) { console.error('Error loading cached audio:', error); }
        return;
      }
    }

    const fullSrc = track.src.startsWith('http') ? track.src : window.location.origin + track.src;
    
    if (audio.src !== fullSrc) {
      console.log('Loading audio track:', track.name, 'from:', fullSrc);
      
      // Try to load the original file first
      try {
        const response = await fetch(fullSrc, { method: 'HEAD' });
        if (response.ok) {
          audio.src = fullSrc;
          // Cache successful loads in background
          if (isCacheSupported) {
            cacheAudio(track.id, fullSrc).catch(console.warn);
          }
        } else {
          console.warn(`Audio file not found: ${fullSrc}, generating demo audio`);
          const demoUrl = createDemoAudioBlob('calm', 30);
          audio.src = demoUrl;
        }
      } catch (error) {
        console.warn(`Failed to check audio file: ${fullSrc}, using demo audio`);
        const demoUrl = createDemoAudioBlob('calm', 30);
        audio.src = demoUrl;
      }
      
      try { 
        audio.load(); 
      } catch (error) {
        console.error('Error loading audio:', error);
      }
    }
  };

  const play = useCallback(async (trackId?: string) => {
    const idToPlay = trackId ?? currentId;
    const track = TRACKS.find(t => t.id === idToPlay) || TRACKS[0];
    setCurrentId(track.id);

    const audio = ensureAudio();
    await loadSrcIfNeeded(audio, track);

    audio.play().then(() => {
      console.log('Audio started playing:', track.name);
      setIsPlaying(true);
    }).catch((error) => {
      console.error('Audio play failed:', error, 'for track:', track.name);
      // Autoplay might be blocked until user gesture
      setIsPlaying(false);
    });
  }, [currentId]);

  const pause = useCallback(() => {
    const audio = audioRef.current;
    if (audio && !audio.paused) {
      audio.pause();
      setIsPlaying(false);
    }
  }, []);

  const stop = useCallback(() => {
    const audio = audioRef.current;
    if (audio) {
      audio.pause();
      audio.currentTime = 0;
      setIsPlaying(false);
    }
  }, []);

  const setTrack = useCallback(async (trackId: string) => {
    setCurrentId(trackId);
    const audio = ensureAudio();
    const track = TRACKS.find(t => t.id === trackId) || TRACKS[0];
    await loadSrcIfNeeded(audio, track);
  }, []);

  const setVolume = useCallback((v: number) => {
    const vol = Math.max(0, Math.min(1, v));
    setVolumeState(vol);
    if (audioRef.current) audioRef.current.volume = vol;
  }, []);

  const toggle = useCallback(async () => {
    const audio = ensureAudio();
    if (audio.paused) {
      await loadSrcIfNeeded(audio, currentTrack);
      audio.play().then(() => setIsPlaying(true)).catch(() => setIsPlaying(false));
    } else {
      audio.pause();
      setIsPlaying(false);
    }
  }, [currentTrack]);

  const cacheAllTracks = useCallback(async () => {
    if (!isCacheSupported) {
      console.warn('Audio caching not supported in this browser');
      return;
    }

    try {
      setIsCaching(true);
      setCacheProgress(0);
      const realTracks = TRACKS.filter(t => !t.src.startsWith('data:'));
      const total = realTracks.length;

      let completed = 0;
      for (const track of realTracks) {
        try {
          const fullSrc = track.src.startsWith('http') ? track.src : window.location.origin + track.src;
          const success = await cacheAudio(track.id, fullSrc);
          if (success) {
            console.log(`Cached: ${track.name}`);
          } else {
            console.warn(`Failed to cache: ${track.name}`);
          }
        } catch (error) {
          console.error(`Error caching ${track.name}:`, error);
        }
        completed += 1;
        setCacheProgress(completed / total);
      }
      
      console.log(`Successfully cached ${completed}/${total} tracks`);
    } finally {
      setIsCaching(false);
    }
  }, [isCacheSupported, cacheAudio]);

  // Auto-cache on mount if supported
  useEffect(() => {
    if (isCacheSupported) {
      // Start caching after a short delay to avoid blocking initial render
      const timer = setTimeout(() => {
        cacheAllTracks();
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [isCacheSupported, cacheAllTracks]);

  const value: FocusMusicContextValue = {
    tracks: TRACKS,
    currentTrack,
    isPlaying,
    volume,
    play,
    pause,
    stop,
    setTrack,
    setVolume,
    toggle,
    cacheAllTracks,
    isCaching,
    cacheProgress,
  };

  return (
    <>
      <audio ref={audioRef} preload="auto" playsInline loop style={{ display: 'none' }} />
      <FocusMusicContext.Provider value={value}>{children}</FocusMusicContext.Provider>
    </>
  );
};

export const useFocusMusic = () => {
  const ctx = useContext(FocusMusicContext);
  if (!ctx) throw new Error('useFocusMusic must be used within FocusMusicProvider');
  return ctx;
};
